import { useRef, useState } from 'react'

import { COLOR_PICKER_DEFAULT_HEX } from '../../../../ColorPicker.constants'
import { useRisingEdgeEffect } from '../../../../ColorPicker.hooks'
import { getRgbFromHex } from '../../../ColorPickerPopover.utils'
import { RGB_CHANNEL } from '../../ColorPickerPopoverPreview.constants'

export function useColorPickerPreviewRgbInputs({
  color,
  isPopoverOpen,
  setIsValueInputFocused,
  tryCommitRgb,
}) {
  const initialRgb = getRgbFromHex(color || COLOR_PICKER_DEFAULT_HEX)

  const [rgbR, setRgbR] = useState(() => String(initialRgb.r))
  const [rgbG, setRgbG] = useState(() => String(initialRgb.g))
  const [rgbB, setRgbB] = useState(() => String(initialRgb.b))
  const [rgbFocusedChannel, setRgbFocusedChannel] = useState(null)

  const rgbGroupRef = useRef(null)

  const [prevColor, setPrevColor] = useState(color)

  // Sync the RGB channel inputs to the `color` prop during render (the
  // documented alternative to syncing via an effect).
  if (color !== prevColor) {
    setPrevColor(color)
    setRgbR(String(initialRgb.r))
    setRgbG(String(initialRgb.g))
    setRgbB(String(initialRgb.b))
  }

  function handlePopoverOpen() {
    setRgbFocusedChannel(null)
  }

  useRisingEdgeEffect({ value: isPopoverOpen, onRisingEdge: handlePopoverOpen })

  function revertRgbFromColorProp() {
    const next = getRgbFromHex(color || COLOR_PICKER_DEFAULT_HEX)

    setRgbR(String(next.r))
    setRgbG(String(next.g))
    setRgbB(String(next.b))
  }

  function handleRFocus() {
    setRgbFocusedChannel(RGB_CHANNEL.r)
  }

  function handleGFocus() {
    setRgbFocusedChannel(RGB_CHANNEL.g)
  }

  function handleBFocus() {
    setRgbFocusedChannel(RGB_CHANNEL.b)
  }

  function handleRgbChannelBlur(event) {
    const related = event.relatedTarget
    const focusStaysInGroup =
      related != null && rgbGroupRef.current?.contains(related)

    if (!focusStaysInGroup) {
      setRgbFocusedChannel(null)
      setIsValueInputFocused(false)

      if (!tryCommitRgb(rgbR, rgbG, rgbB)) {
        revertRgbFromColorProp()
      }
    }
  }

  function handleRChange(event) {
    const next = event.target.value

    setRgbR(next)
    tryCommitRgb(next, rgbG, rgbB)
  }

  function handleGChange(event) {
    const next = event.target.value

    setRgbG(next)
    tryCommitRgb(rgbR, next, rgbB)
  }

  function handleBChange(event) {
    const next = event.target.value

    setRgbB(next)
    tryCommitRgb(rgbR, rgbG, next)
  }

  return {
    handleBChange,
    handleBFocus,
    handleGChange,
    handleGFocus,
    handleRChange,
    handleRFocus,
    handleRgbChannelBlur,
    rgbB,
    rgbFocusedChannel,
    rgbG,
    rgbGroupRef,
    rgbR,
  }
}
